import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { Sales, Tasks, Pdi } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Pill } from "../components/StatusPill";
import SaleUploads from "../components/SaleUploads";
import { TransitHoldBanner } from "../components/TransitHoldBadge";
import { toast } from "sonner";
import {
  ArrowLeft, Wrench, BadgeCheck, ShieldCheck, Clock, Lock, Package,
} from "lucide-react";
import { buildDeliveryDateTime, getTimeRemaining, useTicker } from "../lib/time";
import { cn } from "../lib/utils";

const VIN_REGEX = /^[A-Z0-9]{17}$/;
const ENGINE_REGEX = /^[A-Z0-9]{12}$/;

export default function PdiWorkspace() {
  useTicker(15000);
  const { saleId } = useParams();
  const navigate = useNavigate();
  const { activeUser, locations } = useApp();

  const [sale, setSale] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [vin, setVin] = useState("");
  const [engine, setEngine] = useState("");
  const [busy, setBusy] = useState(false);
  const [readyNotes, setReadyNotes] = useState("");

  const load = useCallback(async () => {
    const [s, t] = await Promise.all([Sales.get(saleId), Tasks.list({ sale_id: saleId })]);
    setSale(s);
    setTasks(t);
    if (!vin) setVin(s.vehicle?.vin || "");
    if (!engine) setEngine(s.vehicle?.engine_no || "");
    // eslint-disable-next-line
  }, [saleId]);

  useEffect(() => { load(); }, [load]);

  if (!sale) return <div className="py-10 text-center text-sm text-slate-500">Loading…</div>;

  const loc = locations.find((l) => l.id === sale.location_id);
  const vinTask = tasks.find((t) => t.type === "PDI_VIN");
  const readyTask = tasks.find((t) => t.type === "PDI_READY");
  const accountsTask = tasks.find((t) => t.type === "Accounts");
  const sparesTask = tasks.find((t) => t.type === "Spares");

  const vinDone = vinTask?.status === "completed";
  const readyDone = readyTask?.status === "completed";

  // Sales Manager: read-only across all form fields + action buttons.
  // Uploads intentionally remain enabled so they can attach docs on behalf.
  const canAct = activeUser?.role === "PDI" || activeUser?.role === "Admin";

  const deliveryAt = buildDeliveryDateTime(sale.delivery?.promised_date, sale.delivery?.promised_time);
  const tr = getTimeRemaining(deliveryAt);

  const releaseForProcessing = async () => {
    if (!VIN_REGEX.test(vin)) return toast.error("VIN must be 17 alphanumeric chars");
    if (!engine.trim()) return toast.error("Engine Number is required");
    if (!ENGINE_REGEX.test(engine)) return toast.error("Engine Number must be exactly 12 alphanumeric characters.");
    setBusy(true);
    try {
      await Pdi.release(saleId, { vin, engine_no: engine, user_id: activeUser?.id });
      toast.success("Released for Processing", {
        description: "Accounts can now begin payment verification.",
        duration: 2800,
      });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const setCheckSheet = async (val) => {
    try {
      await Pdi.setCheckSheet(saleId, val === "yes");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const markReady = async () => {
    setBusy(true);
    try {
      await Pdi.markReady(saleId, { user_id: activeUser?.id, notes: readyNotes || undefined });
      toast.success("Vehicle marked ready for delivery", {
        description: "Final status will reflect once Accounts & Spares complete.",
        duration: 2800,
      });
      setReadyNotes("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="pdi-workspace-page">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <button
            onClick={() => navigate(-1)}
            className="mb-2 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900"
            data-testid="pdi-back-btn"
          >
            <ArrowLeft className="h-3 w-3" /> Back
          </button>
          <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500">
            PDI Workspace
          </div>
          <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight">
            {sale.customer?.name || "—"}
          </h1>
          <div className="mt-1 flex flex-wrap items-center gap-3 text-sm text-slate-500">
            <span className="font-mono-data">{sale.sr_number}</span>
            <span>·</span>
            <span data-testid="pdi-workspace-vehicle">
              {sale.vehicle?.model || "—"}
              {(sale.vehicle?.color_code || sale.vehicle?.color) &&
                ` · ${sale.vehicle.color_code || sale.vehicle.color}`}
            </span>
            <span>·</span>
            <span>{loc?.name} ({loc?.code})</span>
          </div>
        </div>
        <DeliveryTimer tr={tr} delivery={sale.delivery} />
      </div>

      {/* Transit Hold banner — mirrors Sales Detail / Accounts Workbench
          behaviour so PDI team sees why a vehicle's SLA timers are paused. */}
      <TransitHoldBanner sale={sale} />

      {/* Workflow strip */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <WorkflowChip label="VIN" tone={vinDone ? "success" : "warning"} done={vinDone} />
        <WorkflowChip label="Readiness" tone={readyDone ? "success" : "warning"} done={readyDone} />
        <WorkflowChip
          label="Accounts"
          tone={accountsTask?.status === "completed" ? "success" : (accountsTask ? "warning" : "neutral")}
          done={accountsTask?.status === "completed"}
          locked={!accountsTask}
        />
        <WorkflowChip
          label="Spares"
          tone={sparesTask?.status === "completed" ? "success" : "warning"}
          done={sparesTask?.status === "completed"}
        />
      </div>

      {/* Stage 1 */}
      <Card className={cn("border-slate-200", vinDone && "bg-emerald-50/30 border-emerald-200")} data-testid="stage-1-vin">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <div className="flex items-center gap-2">
            <Wrench className="h-5 w-5 text-primary" />
            <CardTitle className="font-display text-xl">Stage 1 · VIN Assignment</CardTitle>
          </div>
          {vinDone ? (
            <Pill tone="success">Released</Pill>
          ) : (
            <Pill tone="warning">Pending</Pill>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-500">
            {vinDone
              ? "VIN already assigned. Accounts has been notified to start processing."
              : "Assign Chassis (VIN) and Engine Number, then release the file so Accounts can start."}
          </p>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                Chassis Number (VIN) *
              </Label>
              <Input
                value={vin}
                onChange={(e) => setVin(e.target.value.toUpperCase())}
                maxLength={17}
                disabled={vinDone || !canAct}
                placeholder="17 alphanumeric"
                className="font-mono-data"
                data-testid="pdi-vin-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                Engine Number *
              </Label>
              <Input
                value={engine}
                onChange={(e) => setEngine(e.target.value.toUpperCase())}
                maxLength={12}
                disabled={vinDone || !canAct}
                placeholder="Exactly 12 alphanumeric"
                className="font-mono-data"
                data-testid="pdi-engine-input"
              />
            </div>
          </div>
          {!vinDone && canAct && (
            <Button
              size="lg"
              onClick={releaseForProcessing}
              disabled={busy || !vin.trim() || !engine.trim()}
              className="w-full gap-2 bg-emerald-600 hover:bg-emerald-700 sm:w-auto"
              data-testid="release-for-processing-btn"
              title={!vin.trim() || !engine.trim()
                ? "Enter Chassis Number (VIN) and Engine Number to enable"
                : undefined}
            >
              <ShieldCheck className="h-4 w-4" /> Release for Processing
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Stage 2 + Accessories side-by-side
          Accessories card is read-only — PDI team uses it as a checklist
          when verifying the vehicle before handover. Stacks on mobile, sits
          on the left at lg+ widths. */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[20rem_minmax(0,1fr)]">
        <AccessoriesRequestedCard sale={sale} />

      {/* Stage 2 */}
      <Card className={cn("border-slate-200", readyDone && "bg-emerald-50/30 border-emerald-200")} data-testid="stage-2-ready">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <div className="flex items-center gap-2">
            <BadgeCheck className="h-5 w-5 text-primary" />
            <CardTitle className="font-display text-xl">Stage 2 · Vehicle Preparation</CardTitle>
          </div>
          {readyDone
            ? <Pill tone="success">Ready</Pill>
            : <Pill tone="warning">Pending</Pill>}
        </CardHeader>
        <CardContent className="space-y-5">
          <p className="text-sm text-slate-500">
            Independent of Stage 1 — but VIN, Engine and the Check Sheet upload are required to mark ready.
          </p>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                PDI Check Sheet Done?
              </Label>
              <Select
                value={sale.pdi_check_sheet_done ? "yes" : "no"}
                onValueChange={setCheckSheet}
                disabled={readyDone || !canAct}
              >
                <SelectTrigger data-testid="pdi-check-sheet-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-3">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">
              Documents & Photos
            </Label>
            <SaleUploads
              saleId={saleId}
              uploadedBy={activeUser?.id}
              types={[
                "pdi_check_sheet",
                "vehicle_photo_front", "vehicle_photo_left", "vehicle_photo_right",
                "vehicle_photo_rear", "vin_photo",
              ]}
              readOnly={readyDone}
            />
          </div>

          {!readyDone && canAct && (
            <>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                  Notes (optional)
                </Label>
                <Textarea
                  rows={2}
                  value={readyNotes}
                  onChange={(e) => setReadyNotes(e.target.value)}
                  placeholder="Any observations during preparation"
                  data-testid="pdi-ready-notes"
                />
              </div>
              <Button
                size="lg"
                onClick={markReady}
                disabled={busy}
                className="w-full gap-2 bg-emerald-600 hover:bg-emerald-700 sm:w-auto"
                data-testid="mark-ready-btn"
              >
                <BadgeCheck className="h-4 w-4" /> Mark Ready for Delivery
              </Button>
            </>
          )}
        </CardContent>
      </Card>
      </div>
    </div>
  );
}

// ──────────────── Accessories Requested (PDI read-only) ────────────────
// Auto-synced with `sale.accessory_items` so it reflects any updates made by
// Sales / Admin (e.g. items added post-release) or by the Spares team during
// fulfilment. The optional "fulfilled" count derives from the same
// `sale.spares.items[i].status === 'assigned'` field the Spares Workbench
// writes — when Spares hasn't started yet, only the requested count is shown.
function AccessoriesRequestedCard({ sale }) {
  const items = sale?.accessory_items || [];
  const total = items.length;
  const sparesItems = sale?.spares?.items || [];
  const sparesByName = Object.fromEntries(
    sparesItems.map((s) => [(s.name || "").trim().toLowerCase(), s]),
  );
  const fulfilledCount = sparesItems.filter((s) => s.status === "assigned").length;
  const sparesStarted = sparesItems.length > 0;

  return (
    <Card className="border-slate-200 bg-amber-50/30" data-testid="pdi-accessories-card">
      <CardHeader className="space-y-1.5 pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Package className="h-5 w-5 text-amber-700" />
            <CardTitle className="font-display text-base">Accessories Requested</CardTitle>
          </div>
          <span
            className="rounded-full bg-amber-100 px-2 py-0.5 font-mono-data text-[11px] font-bold text-amber-900"
            data-testid="pdi-accessories-count"
          >
            {total}
          </span>
        </div>
        <p className="text-[11px] text-slate-500">
          Verify these items are physically present before customer handover.
        </p>
        {sparesStarted && total > 0 && (
          <div className="flex items-center gap-2 pt-1 text-[11px] font-semibold">
            <span className="rounded-md bg-white px-2 py-0.5 text-slate-700 ring-1 ring-slate-200">
              Requested · {total}
            </span>
            <span className={cn(
              "rounded-md px-2 py-0.5 ring-1",
              fulfilledCount === total
                ? "bg-emerald-100 text-emerald-800 ring-emerald-200"
                : "bg-amber-100 text-amber-800 ring-amber-200",
            )}>
              Fulfilled · {fulfilledCount}/{total}
            </span>
          </div>
        )}
      </CardHeader>
      <CardContent className="pt-0">
        {total === 0 ? (
          <div
            className="rounded-md border border-dashed border-slate-300 bg-white/60 px-3 py-6 text-center text-sm text-slate-500"
            data-testid="pdi-accessories-empty"
          >
            No Accessories Requested
          </div>
        ) : (
          <ul className="space-y-1.5" data-testid="pdi-accessories-list">
            {items.map((it, i) => {
              const sp = sparesByName[(it.name || "").trim().toLowerCase()];
              const status = sp?.status;
              return (
                <li
                  key={`acc-${i}`}
                  className="flex items-start justify-between gap-3 rounded-md bg-white px-3 py-2 text-sm ring-1 ring-slate-100"
                  data-testid={`pdi-accessory-${i}`}
                >
                  <span className="font-medium text-slate-800 truncate">{it.name || "—"}</span>
                  {status === "assigned" ? (
                    <span className="flex-shrink-0 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-800">
                      Assigned
                    </span>
                  ) : status === "not_available" ? (
                    <span className="flex-shrink-0 rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-rose-800">
                      Not Avail.
                    </span>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function DeliveryTimer({ tr, delivery }) {
  const tones = {
    success: "border-emerald-200 bg-emerald-50 text-emerald-700",
    warning: "border-amber-200 bg-amber-50 text-amber-700",
    danger: "border-red-200 bg-red-50 text-red-700",
    neutral: "border-slate-200 bg-slate-50 text-slate-700",
  };
  return (
    <div className={cn("rounded-lg border px-4 py-3", tones[tr.tone])}>
      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.18em]">
        <Clock className="h-3 w-3" /> Time Remaining
      </div>
      <div className="mt-1 font-mono-data text-2xl font-semibold tracking-tight">
        {tr.label}
      </div>
      <div className="text-[11px] text-slate-500">
        Promised {delivery?.promised_date} · {delivery?.promised_time || "—"}
      </div>
    </div>
  );
}

function WorkflowChip({ label, tone, done, locked }) {
  const tones = {
    success: "border-emerald-200 bg-emerald-50 text-emerald-800",
    warning: "border-amber-200 bg-amber-50 text-amber-800",
    neutral: "border-slate-200 bg-slate-100 text-slate-500",
  };
  return (
    <div className={cn("flex items-center justify-between rounded-md border px-3 py-2 text-xs font-semibold", tones[tone])}>
      <span>{label}</span>
      {locked
        ? <Lock className="h-3.5 w-3.5" />
        : <span className={cn("h-2 w-2 rounded-full",
            done ? "bg-emerald-500" : tone === "neutral" ? "bg-slate-400" : "bg-amber-500")} />}
    </div>
  );
}
